export const EXTERNAL_NAVIGATION_REQUEST_EVENT = 'newsrooms:external-navigation-request'

const DEFAULT_TARGET = '_blank'
const DEFAULT_FEATURES = 'noopener,noreferrer'

const HTTP_PROTOCOLS = new Set(['http:', 'https:'])
const URL_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+.-]*:/
const REDIRECT_QUERY_PARAM_KEYS = [
  'url',
  'q',
  'u',
  'target',
  'dest',
  'destination',
  'redirect',
  'redirect_url',
  'redirect_uri',
  'r'
]
const MAX_REDIRECT_DEPTH = 3
const REDIRECT_PATH_HINT_PATTERN = /\/(url|redirect|out|external|away|goto|link)(\/|$)/i

export interface ExternalNavigationRequestDetail {
  url: string
  target?: string
  features?: string
}

const getWindowOrigin = (): string => {
  if (typeof window === 'undefined') return ''
  return window.location.origin
}

const getWindowHref = (): string => {
  if (typeof window === 'undefined') return ''
  return window.location.href
}

const hasHttpProtocol = (url: URL): boolean => HTTP_PROTOCOLS.has(url.protocol)

const isGoogleHost = (hostname: string): boolean => {
  return (
    hostname === 'google.com' ||
    hostname.endsWith('.google.com') ||
    hostname === 'googleapis.com' ||
    hostname.endsWith('.googleapis.com')
  )
}

const looksLikeHostnameWithoutProtocol = (value: string): boolean => {
  const source = value.trim()
  if (!source) return false
  if (
    source.startsWith('/') ||
    source.startsWith('./') ||
    source.startsWith('../') ||
    source.startsWith('?') ||
    source.startsWith('#')
  ) {
    return false
  }

  const boundaryIndex = source.search(/[/?#]/)
  const hostAndPort = boundaryIndex >= 0 ? source.slice(0, boundaryIndex) : source
  if (!hostAndPort || hostAndPort.includes('@')) return false

  const hostParts = hostAndPort.split(':')
  if (hostParts.length > 2) return false

  const host = hostParts[0]
  const port = hostParts[1]
  if (!host) return false

  if (port) {
    const parsedPort = Number.parseInt(port, 10)
    if (!Number.isInteger(parsedPort) || parsedPort < 0 || parsedPort > 65535) return false
  }

  if (host === 'localhost') return true

  const segments = host.split('.').filter(Boolean)
  if (segments.length === 0) return false

  const allSegmentsNumeric = segments.every(segment => /^[0-9]+$/.test(segment))
  if (allSegmentsNumeric && segments.length === 4) {
    return segments.every(segment => {
      const segmentValue = Number.parseInt(segment, 10)
      return segmentValue >= 0 && segmentValue <= 255
    })
  }

  if (segments.length < 2) return false

  const allSegmentsValid = segments.every(segment => /^[a-zA-Z0-9-]+$/.test(segment))
  if (!allSegmentsValid) return false

  const tld = segments[segments.length - 1]
  return tld.length >= 2 && /[a-zA-Z]/.test(tld)
}

const isLikelyExternalUrlCandidate = (value: string): boolean => {
  const source = value.trim()
  if (!source) return false
  if (source.startsWith('//')) return true
  if (URL_SCHEME_PATTERN.test(source)) return true
  return looksLikeHostnameWithoutProtocol(source)
}

const getParseCandidates = (source: string, allowRelative: boolean): string[] => {
  const candidates: string[] = []
  const hasExplicitScheme = URL_SCHEME_PATTERN.test(source)
  const hasProtocolRelativePrefix = source.startsWith('//')
  const hasHostnameWithoutProtocol = looksLikeHostnameWithoutProtocol(source)

  if (hasProtocolRelativePrefix) {
    candidates.push(`https:${source}`)
  }

  if (!hasExplicitScheme && hasHostnameWithoutProtocol) {
    candidates.push(`https://${source}`)
  }

  if (
    allowRelative ||
    hasExplicitScheme ||
    hasProtocolRelativePrefix ||
    hasHostnameWithoutProtocol
  ) {
    candidates.push(source)
  }

  return [...new Set(candidates)]
}

const parseHttpUrl = (rawSource: string, allowRelative: boolean): URL | null => {
  const source = rawSource.trim()
  if (!source) return null

  const parseCandidates = getParseCandidates(source, allowRelative)

  for (const candidate of parseCandidates) {
    try {
      const parsed = new URL(candidate, getWindowHref() || undefined)
      if (hasHttpProtocol(parsed)) return parsed
    } catch {
      // Continue to next candidate.
    }
  }

  return null
}

const resolveRedirectCandidate = (url: URL): string | null => {
  const origin = getWindowOrigin()
  const shouldInspectQuery =
    isGoogleHost(url.hostname) ||
    url.origin !== origin ||
    REDIRECT_PATH_HINT_PATTERN.test(url.pathname)

  if (!shouldInspectQuery) return null

  const orderedKeys = isGoogleHost(url.hostname)
    ? ['url', 'q', ...REDIRECT_QUERY_PARAM_KEYS.filter(key => key !== 'url' && key !== 'q')]
    : REDIRECT_QUERY_PARAM_KEYS

  for (const key of orderedKeys) {
    const value = url.searchParams.get(key)?.trim()
    if (!value || !isLikelyExternalUrlCandidate(value)) continue
    return value
  }

  return null
}

const unwrapRedirectUrl = (parsed: URL): URL => {
  let current = parsed

  for (let depth = 0; depth < MAX_REDIRECT_DEPTH; depth += 1) {
    const redirectCandidate = resolveRedirectCandidate(current)
    if (!redirectCandidate) break

    const unwrapped = parseHttpUrl(redirectCandidate, false)
    if (!unwrapped) break

    current = unwrapped
  }

  return current
}

export const resolveExternalHttpUrl = (rawUrl: string): string | null => {
  const source = rawUrl.trim()
  if (!source || source.startsWith('#')) return null

  const parsed = parseHttpUrl(source, true)
  if (!parsed) return null

  const finalUrl = unwrapRedirectUrl(parsed)
  if (finalUrl.origin === getWindowOrigin()) return null

  return finalUrl.toString()
}

export const requestExternalNavigation = (
  rawUrl: string,
  options?: { target?: string; features?: string }
): boolean => {
  const externalUrl = resolveExternalHttpUrl(rawUrl)
  if (!externalUrl || typeof window === 'undefined') return false

  const detail: ExternalNavigationRequestDetail = {
    url: externalUrl,
    target: options?.target || DEFAULT_TARGET,
    features: options?.features || DEFAULT_FEATURES
  }

  window.dispatchEvent(
    new CustomEvent<ExternalNavigationRequestDetail>(EXTERNAL_NAVIGATION_REQUEST_EVENT, { detail })
  )
  return true
}

export const navigateToExternal = (request: ExternalNavigationRequestDetail): void => {
  if (typeof window === 'undefined') return

  const target = request.target || DEFAULT_TARGET
  const features = request.features || DEFAULT_FEATURES

  if (target === '_self') {
    window.location.assign(request.url)
    return
  }

  window.open(request.url, target, features)
}
